// This file contains helper functions for connecting to the database and retrieving data.
// It is responsible for establishing a connection to the database, executing queries,
// and returning the results to the application.

import axios from 'axios';

// Function returns the number of tickets currently in the queue
// by making a GET request to the server.
export async function getTicketsInQueueCount(connectionURL) {
  try {
    const response = await axios.get(`${connectionURL}/checkOuts/checkOutsCountSPToday`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch tickets in queue count: ${response.statusText}`);
    }
    if (typeof response.data !== 'number') {
      throw new Error('Invalid response format for tickets in queue count');
    }
    if (response.data < 0) {
      throw new Error('Invalid tickets in queue count: count cannot be negative');
    }
    if (response.data === 0) {
      console.warn('No tickets in queue: count is zero');
    }
    if (response.data > 500) {
      console.warn('High tickets in queue count: count exceeds 500');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching tickets in queue count:', error);
    return null;
  }
}

// Get the list of checkouts in the table by making a GET request to the server.
export async function getCheckouts(connectionURL) {
  try {
    const response = await axios.get(`${connectionURL}/checkOuts/allCheckOutsSP`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch checkouts: ${response.statusText}`);
    }
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format for checkouts list');
    }
    if (response.data.length === 0) {
      console.warn('No checkouts found: checkouts list is empty');
    }
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching checkouts:', error);
    return null;
  }
}

export async function getPendingCheckouts(connectionURL) {
  try {
    const response = await axios.get(`${connectionURL}/checkOuts/PendingCheckOutsInQueue`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch pending checkouts: ${response.statusText}`);
    }
    // if (!Array.isArray(response.data)) {
    //   throw new Error('Invalid response format for pending checkouts list');
    // }
    // if (response.data.length === 0) {
    //   console.warn('No pending checkouts found: pending checkouts list is empty');
    // }
    if (response.data) {
      return response.data || 0;
    }
  } catch (error) {
    console.error('Error fetching pending checkouts:', error);
    return null;
  }
}

// Update tblCheckOuts by making a PATCH request to the server
//  with the ticket number and new status.
// This function is used to update the status of a ticket in the database.
// When ticket is scanned, the status is updated to "Exited"
// and the time is recorded in the database.
export async function updateTicketStatus(connectionURL, ticketNumber) {
  try {
    const response = await axios.patch(`${connectionURL}/checkOuts/updateExitTime/${ticketNumber}`, {
      Ticket: ticketNumber,
    });
    if (response.status !== 200) {
      throw new Error(`Failed to update ticket status: ${response.statusText}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error updating ticket status:', error);
    return null;
  }
}

// Get average wait time for the day by making a GET request to the server.
// This function is used to retrieve the average wait time for the day from the database.
// It can be used to display the average wait time in the application.
export async function getAverageWaitTime(connectionURL) {
  try {
    const response = await axios.get(`${connectionURL}/checkOuts/waitTimeStatsSPToday`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch average wait time: ${response.statusText}`);
    }
    if (typeof response.data !== 'number') {
      throw new Error('Invalid response format for average wait time');
    }
    if (response.data < 0) {
      throw new Error('Invalid average wait time: time cannot be negative');
    }
    if (response.data === 0) {
      console.warn('No wait time data available: average wait time is zero');
    }
    if (response.data) {
      console.log('Average wait time:', response.data);
      return Math.round(response.data);
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching average wait time:', error);
    return null;
  }
}
// Check if ticket exists in check out table by making a
// GET request to the server with the ticket number.
export async function checkTicketExists(connectionURL, ticketNumber) {
  try {
    const response = await axios.get(`${connectionURL}/checkOuts/FindTicket/${ticketNumber}`);
    if (response.status !== 200) throw new Error(`Failed to check if ticket exists: ${response.statusText}`);

    // if (typeof response.data !== 'boolean') {
    //   throw new Error('Invalid response format for ticket existence check')
    // }
    console.log('Ticket exists:', response.data.TRUE);
    return response.data.TRUE;
  } catch (error) {
    console.error('Error checking if ticket exists:', error);
    return false;
  }
}

// Get exceptions for a ticket by making a GET request to the server.
export async function getTicketExceptions(connectionURL, ticketNumber) {
  try {
    const response = await axios.get(`${connectionURL}/exceptions/exceptionsByTicket/${ticketNumber}`);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch ticket exceptions: ${response.statusText}`);
    }
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format for ticket exceptions');
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket exceptions:', error);
    return null;
  }
}

// Get all exceptions for a date range by making a GET request
// to the server with the start and end dates.
export async function getExceptionsByDateRange(connectionURL, startDate, endDate) {
  try {
    const response = await axios.get(
      `${connectionURL}/exceptions/allExceptionsByDateRange/${startDate}&${endDate}`,
    );
    if (response.status !== 200) {
      throw new Error(`Failed to fetch exceptions by date range: ${response.statusText}`);
    }
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format for exceptions by date range');
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching exceptions by date range:', error);
    return null;
  }
}

// Post a new exception to the database by making a POST request to the server
// with the ticket number, exception type and description.
export async function postNewException(connectionURL, ticketNumber, exceptionDate, desc) {
  try {
    const response = await axios.post(`${connectionURL}/exceptions/addExceptions`, {
      Ticket: ticketNumber,
      ExceptionDateTime: exceptionDate,
      ExceptionType: desc,
    });
    if (response.status !== 201 && response.status !== 200) {
      throw new Error(`Failed to post new exception: ${response.statusText}`);
    }
    return response.data;
  } catch (error) {
    console.error('Error posting new exception:', error);
    return null;
  }
}

// Confirm that the user exists in the database by making a GET request to the server with the username and password.
export async function confirmUserExists(connectionURL, username) {
  console.log(
    'Confirming user exists for username:',
    `${connectionURL}/usersLogin/getUserByUserName/${username}`,
    username,
  );
  try {
    const response = await axios.get(`${connectionURL}/usersLogin/getUserByUserName/${username}`);
    if (response.status !== 200) {
      throw new Error(`Failed to confirm user exists: ${response.statusText}`);
    }
    console.log(
      'Confirming user exists for username:',
      `${connectionURL}/usersLogin/getUserByUserName/${username}`,
      username,
    );
    // if (typeof response.data !== 'boolean') {
    //   throw new Error('Invalid response format for user existence check');
    // }
    // console.log('User exists:', response.data.TRUE);
    // return response.data.TRUE;
    if (response.status === 200) {
      return true;
    }
  } catch (error) {
    console.error('Error confirming user exists:', error);
    return null;
  }
}

// Post username and password to the server for authentication by making a POST request to the server.
export async function authenticateUser(connectionURL, username, password) {
  console.log(
    'Authenticating user with username:',
    username,
    'and password:',
    password,
    `${connectionURL}/usersLogin/login`,
  );
  try {
    const response = await axios.post(`${connectionURL}/usersLogin/login`, {
      username: username,
      pass: password,
    });
    if (response.status !== 200) {
      throw new Error(`Failed to authenticate user: ${response.statusText}`);
    }
    if (typeof response.data !== 'object' || response.data === null) {
      throw new Error('Invalid response format for user authentication');
    }
    console.log('User authenticated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error authenticating user:', error);
    return null;
  }
}
